import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Stack, Typography } from '@mui/material';

import { useTranslation } from 'react-i18next';
import { fetchCustomerSummary, fetchUserSummary } from './analyticsApi';
import type { CustomerSummaryRow, UserSummaryRow } from './analyticsTypes';
import { readAxiosDetail } from '../api_calls';
import RankedList, { RankedListItem } from './components/RankedList';
import { useAnalyticsFilters, previousPeriod } from './hooks/useAnalyticsFilters';
import { useInspector } from './hooks/useInspector';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../utils/iconFallbacks';
const PAGE_SIZE = 100;

export default function UsageSection() {
  const { t } = useTranslation('admin');
  const { filters } = useAnalyticsFilters();
  const inspector = useInspector();
  const [users, setUsers] = useState<UserSummaryRow[]>([]);
  const [customers, setCustomers] = useState<CustomerSummaryRow[]>([]);
  const [prevUsers, setPrevUsers] = useState<UserSummaryRow[]>([]);
  const [prevCustomers, setPrevCustomers] = useState<CustomerSummaryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const since = filters.since;
  const until = filters.until;
  const compare = filters.compare;
  const prevRange = compare && since && until ? previousPeriod(since, until) : null;

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    const fetchCurrent = Promise.all([
      fetchCustomerSummary({ page_size: PAGE_SIZE, since, until }),
      fetchUserSummary({ page_size: PAGE_SIZE, since, until }),
    ]);

    const fetchPrev = prevRange
      ? Promise.all([
          fetchCustomerSummary({ page_size: PAGE_SIZE, ...prevRange }),
          fetchUserSummary({ page_size: PAGE_SIZE, ...prevRange }),
        ])
      : Promise.resolve(null);

    Promise.all([fetchCurrent, fetchPrev])
      .then(([current, prev]) => {
        if (controller.signal.aborted) return;
        setCustomers(current[0].items);
        setUsers(current[1].items);
        if (prev) {
          setPrevCustomers(prev[0].items);
          setPrevUsers(prev[1].items);
        } else {
          setPrevCustomers([]);
          setPrevUsers([]);
        }
        setLoading(false);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(readAxiosDetail(err));
        setLoading(false);
      });

    return () => controller.abort();
  }, [since, until, compare, prevRange?.since, prevRange?.until]);

  const customerItems = useMemo<RankedListItem[]>(() => {
    const prevMap = new Map<string, number>();
    prevCustomers.forEach((c) => {
      if (c.customer_id) prevMap.set(c.customer_id, c.total_events);
    });
    return customers.map((c) => ({
      id: c.customer_id ?? c.customer_name ?? '',
      primary: c.customer_name ?? c.customer_id ?? '—',
      secondary: c.customer_id ?? undefined,
      value: c.total_events,
      previousValue: c.customer_id ? prevMap.get(c.customer_id) ?? null : null,
    }));
  }, [customers, prevCustomers]);

  const userItems = useMemo<RankedListItem[]>(() => {
    const prevMap = new Map<string, number>();
    prevUsers.forEach((u) => {
      if (u.actor_id) prevMap.set(u.actor_id, u.total_events);
    });
    return users.map((u) => ({
      id: u.actor_id ?? u.username ?? '',
      primary: u.username ?? u.actor_id ?? '—',
      secondary: u.actor_id ?? undefined,
      value: u.total_events,
      previousValue: u.actor_id ? prevMap.get(u.actor_id) ?? null : null,
    }));
  }, [users, prevUsers]);

  const totalCustomers = customers.length;
  const totalUsers = users.length;

  return (
    <Stack spacing={3}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
        <Typography variant="body2" color="text.secondary">
          {t('analytics.usage.activeCustomers', { count: totalCustomers })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('analytics.usage.activeUsers', { count: totalUsers })}
        </Typography>
        {compare && prevRange && (
          <Typography variant="caption" color="text.disabled">
            {t('analytics.usage.comparingTo', { since: prevRange.since, until: prevRange.until })}
          </Typography>
        )}
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3 }}>
        <RankedList
          title={t('analytics.usage.topCustomers')}
          icon={<Icon d={ICON_FALLBACKS.Business} size={20} />}
          items={customerItems}
          loading={loading}
          limit={50}
          onItemClick={(item) => {
            if (item.id) inspector.open({ kind: 'customer', customerId: item.id, customerName: item.primary });
          }}
          emptyTitle={t('analytics.usage.noCustomerActivity')}
          emptyBody={t('analytics.usage.widenRange')}
        />
        <RankedList
          title={t('analytics.usage.topUsers')}
          icon={<Icon name="users" size={20} />}
          items={userItems}
          loading={loading}
          limit={50}
          onItemClick={(item) => {
            if (item.id) inspector.open({ kind: 'user', actorId: item.id, username: item.primary });
          }}
          emptyTitle={t('analytics.usage.noUserActivity')}
          emptyBody={t('analytics.usage.widenRange')}
        />
      </Box>
    </Stack>
  );
}
